import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase, isSupabaseEnabled } from '../lib/supabase';
import { PresenceContext } from './presenceContextValue';
import { useAuth } from './useAuth';

// ─────────────────────────────────────────────────────────────────────────────
// PresenceContext — tracks which users currently have the app open,
// which page they're on, and (when on an owner dashboard) which deal
// they're viewing. Uses Supabase Realtime presence so every peer sees
// updates in near real time. Exposes:
//   - peers: [{ id, name, email, avatarUrl, page, viewingDealId, color, onSince }]
//            — does NOT include the current user; use `me` for that.
//   - me:    { id, name, email, color, page }
//   - setViewingDealId(dealId | null): the Owner Dashboard calls this on
//            mount so kanban cards on every other user's Deal Tracker
//            can show a small avatar indicating "so-and-so is viewing
//            this deal right now". Clears on unmount.
//   - colorFor(userId): deterministic hex from the user id, reused in
//            the header presence stack AND the per-card indicators so
//            a user reads as the same color everywhere.
// ─────────────────────────────────────────────────────────────────────────────

// Pre-picked palette with enough contrast on both the navy grid
// backdrop and white kanban cards. 10 is plenty — we only have 5
// real users in practice.
const COLOR_PALETTE = [
  '#5BBFEF', '#f472b6', '#34d399', '#fbbf24', '#60a5fa',
  '#fb923c', '#60a5fa', '#f87171', '#22d3ee', '#c084fc',
];

function hashString(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
function colorForId(id) {
  if (!id) return '#64748b';
  return COLOR_PALETTE[hashString(String(id)) % COLOR_PALETTE.length];
}

// Map each top-level route to a human label shown in the peer list.
function labelForPath(pathname) {
  if (!pathname) return 'Signed in';
  if (pathname.startsWith('/deals')) return 'Deal Tracker';
  if (pathname.startsWith('/owners/')) return 'Owner Dashboard';
  if (pathname.startsWith('/flowcharts')) return 'Flowcharts';
  if (pathname.startsWith('/documents')) return 'Documents';
  if (pathname.startsWith('/map')) return 'Map';
  if (pathname.startsWith('/buyers')) return 'Buyers';
  if (pathname.startsWith('/closings')) return 'Closings';
  if (pathname.startsWith('/admin')) return 'Admin';
  if (pathname.startsWith('/accounting')) return 'Accounting';
  if (pathname.startsWith('/assets-under-management')) return 'Assets Under Management';
  return 'Signed in';
}

/**
 * Whether two peer lists say the same thing.
 *
 * Field by field rather than by reference, because the list is rebuilt
 * from scratch on every sync event: the objects are always new even
 * when the people and what they are doing are identical. Compared in
 * the order the list is already sorted in, so this is O(n) over a
 * handful of colleagues.
 */
function samePeers(a = [], b = []) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i];
    const y = b[i];
    if (x.id !== y.id
      || x.name !== y.name
      || x.email !== y.email
      || x.avatarUrl !== y.avatarUrl
      || x.page !== y.page
      || x.viewingDealId !== y.viewingDealId
      || x.color !== y.color
      || x.onSince !== y.onSince) return false;
  }
  return true;
}

export function PresenceProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [peers, setPeers] = useState([]);
  const [viewingDealId, setViewingDealId] = useState(null);
  const channelRef = useRef(null);
  // Keep the latest identity / page / dealId in refs so the track()
  // calls from different effects always ship the full picture even
  // if state updates haven't flushed yet.
  const mePayloadRef = useRef(null);

  const me = useMemo(() => {
    if (!user) return null;
    return {
      id: user.id,
      name: user.name || user.email || 'Unknown',
      email: user.email || '',
      avatarUrl: user.avatarUrl || '',
      color: colorForId(user.id),
    };
  }, [user]);

  const buildTrackPayload = useCallback(() => ({
    name: me?.name || '',
    email: me?.email || '',
    avatarUrl: me?.avatarUrl || '',
    page: labelForPath(location.pathname),
    viewingDealId: viewingDealId || null,
    onSince: mePayloadRef.current?.onSince || new Date().toISOString(),
  }), [me, location.pathname, viewingDealId]);

  // Subscribe once per signed-in user.
  useEffect(() => {
    if (!isSupabaseEnabled || !me) {
      setPeers([]);
      return undefined;
    }
    const ch = supabase.channel('presence:app', {
      config: { presence: { key: me.id } },
    });
    channelRef.current = ch;
    const syncFromState = () => {
      const state = ch.presenceState() || {};
      const next = [];
      for (const key of Object.keys(state)) {
        if (key === me.id) continue;
        const entries = state[key] || [];
        const last = entries[entries.length - 1];
        if (!last) continue;
        next.push({
          id: key,
          name: last.name || last.email || 'Unknown',
          email: last.email || '',
          avatarUrl: last.avatarUrl || '',
          page: last.page || 'Signed in',
          viewingDealId: last.viewingDealId || null,
          color: colorForId(key),
          onSince: last.onSince || null,
        });
      }
      next.sort((a, b) => a.name.localeCompare(b.name));
      // Same people, same array. Supabase fires a presence sync on every
      // join, leave and heartbeat from anyone in the workspace, and a
      // fresh array each time is a fresh context value, which re-renders
      // every consumer — on the Deal Tracker that is a presence pill on
      // all 129 cards, several times a minute, for a list that usually
      // has not changed at all.
      setPeers((prev) => (samePeers(prev, next) ? prev : next));
    };
    ch.on('presence', { event: 'sync' }, syncFromState);
    ch.on('presence', { event: 'join' }, syncFromState);
    ch.on('presence', { event: 'leave' }, syncFromState);
    ch.subscribe(async (status) => {
      if (status !== 'SUBSCRIBED') return;
      const payload = buildTrackPayload();
      mePayloadRef.current = payload;
      try { await ch.track(payload); }
      catch (err) { console.warn('[presence] initial track failed:', err); }
    });
    return () => {
      try { ch.untrack(); } catch { /* ignore */ }
      try { supabase.removeChannel(ch); } catch { /* ignore */ }
      channelRef.current = null;
      mePayloadRef.current = null;
      setPeers([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id]);

  // Push updates when page OR viewingDealId changes. Debounced 400ms
  // so a rapid sequence of nav changes (e.g. kanban drag-drop fires
  // viewingDealId on/off as the cursor crosses cards) collapses to
  // a single track() rather than hammering the channel with 5+
  // updates per interaction. The trailing call wins, so the final
  // page/dealId is always what other peers see.
  useEffect(() => {
    const ch = channelRef.current;
    if (!ch || !me) return undefined;
    const handle = setTimeout(() => {
      const payload = buildTrackPayload();
      mePayloadRef.current = payload;
      ch.track(payload).catch((err) => console.warn('[presence] track update failed:', err));
    }, 400);
    return () => clearTimeout(handle);
  }, [location.pathname, viewingDealId, me, buildTrackPayload]);

  const value = useMemo(() => ({
    peers,
    me: me ? { ...me, page: labelForPath(location.pathname) } : null,
    setViewingDealId,
    colorFor: colorForId,
  }), [peers, me, location.pathname]);

  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  );
}
